// ============================================================================
//  EligibilityOverview — what NHIA actually said about the renewals
// ============================================================================
//
//  The register says what WE did. This says whether it worked, which is a
//  different question and the one the analysis is about.
//
//  A renewal has three endings, not two:
//
//    LIVE NOW      a CCC came back and eligibility has started.
//    STARTS LATER  a valid answer whose start date is in the FUTURE. A
//                  membership that lapsed for a long time takes two weeks to a
//                  month to come alive. Nothing is wrong; it is not ready.
//    DID NOT WORK  no CCC, an unknown card, or the call failed.
//
//  The middle one is why this screen exists. Without it, staff re-phone people
//  who are already renewed, and the campaign's own numbers are wrong in both
//  directions.
//
//  ── THE CALENDAR IS THE POINT ──────────────────────────────────────────────
//
//  "22 starting later" is not actionable. "9 on 15 October, 13 on 28 October"
//  tells somebody when to look again, which is the only thing to DO with a
//  membership that is waiting.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Chip, Collapse, IconButton, Tooltip, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { eligibilityOverview } from '../BaseURL';

const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const LINE = '#E7EBF0';

const OUT = {
  active:       { fg: '#15803D', bg: '#ECFDF3', bd: '#86EFAC', label: 'CCC issued' },
  starts_later: { fg: '#B45309', bg: '#FFF7ED', bd: '#FDBA74', label: 'Starts later' },
  no_ccc:       { fg: '#B91C1C', bg: '#FEF2F2', bd: '#FCA5A5', label: 'No CCC' },
  not_found:    { fg: '#B91C1C', bg: '#FEF2F2', bd: '#FCA5A5', label: 'Not found' },
  failed:       { fg: '#475569', bg: '#F1F5F9', bd: '#CBD5E1', label: 'Call failed' },
};

//  `compact` is set when this sits in the left rail rather than across the
//  page. Five tiles in a 360px column would be five slivers, so it drops to two
//  per row and the date calendar wraps instead of scrolling.
export default function EligibilityOverview({ campaign, refreshKey, compact = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(eligibilityOverview, {
        params: campaign ? { campaign } : {},
      });
      setData(d); setErr(null);
    } catch (e) {
      //  A facility that has never run a check must not see an error for it.
      setData(null);
      setErr(e?.response?.data?.message || null);
    }
  }, [campaign]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const t = data?.totals;
  //  Nothing checked yet is not a state worth a panel.
  if (err || !t || !t.checked) return null;

  const tiles = [
    { key: 'active', n: t.active, sub: t.active_today ? `${t.active_today} today` : null },
    { key: 'starts_later', n: t.starts_later },
    { key: 'no_ccc', n: t.no_ccc },
    { key: 'not_found', n: t.not_found },
    { key: 'failed', n: t.failed },
  ].filter((x) => Number(x.n) > 0);

  return (
    <Paper elevation={0} sx={{
      borderRadius: 3, border: `1px solid ${LINE}`, background: '#fff',
      boxShadow: '0 1px 2px rgba(15,23,42,.04)', mb: 2, overflow: 'hidden',
    }}>
      <Box onClick={() => setOpen((v) => !v)} sx={{
        px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1,
        cursor: 'pointer', userSelect: 'none',
        '&:hover': { background: '#FBFCFD' },
      }}>
        <EventAvailableIcon sx={{ fontSize: 18, color: MUTED }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: INK }}>
          What NHIA said
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: FAINT }}>
          {Number(t.checked).toLocaleString()} checked
        </Typography>
        <Box sx={{ flex: 1 }} />
        {!open && !compact && tiles.map((x) => (
          <Box key={x.key} component="span" sx={{
            px: .9, py: .2, borderRadius: 999, fontSize: 10.5, fontWeight: 800,
            background: OUT[x.key].bg, color: OUT[x.key].fg,
            border: `1px solid ${OUT[x.key].bd}`,
          }}>{OUT[x.key].label} {Number(x.n).toLocaleString()}</Box>
        ))}
        <IconButton size="small">
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Divider sx={{ borderColor: LINE }} />
        <Box sx={{ p: 2 }}>
          <Box sx={{
            display: 'grid', gap: 1, mb: data?.upcoming?.length ? 2 : 0,
            gridTemplateColumns: compact
              ? 'repeat(2, 1fr)'
              : { xs: 'repeat(2, 1fr)', sm: `repeat(${Math.min(tiles.length, 5)}, 1fr)` },
          }}>
            {tiles.map((x) => {
              const o = OUT[x.key];
              return (
                <Box key={x.key} sx={{
                  p: 1.25, borderRadius: 2, background: o.bg,
                  border: `1px solid ${o.bd}`,
                }}>
                  <Typography noWrap sx={{
                    fontSize: 9.5, fontWeight: 800, letterSpacing: .4,
                    textTransform: 'uppercase', color: o.fg,
                  }}>{o.label}</Typography>
                  <Typography sx={{
                    fontSize: 24, fontWeight: 800, lineHeight: 1.1, color: o.fg,
                  }}>{Number(x.n).toLocaleString()}</Typography>
                  {x.sub && (
                    <Typography noWrap sx={{ fontSize: 10, color: o.fg, opacity: .75 }}>
                      {x.sub}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/*  WHEN the waiting ones come alive. "22 starting later" is not
              something anybody can act on; a date is. */}
          {data.upcoming?.length > 0 && (
            <>
              <Typography sx={{
                fontSize: 10.5, fontWeight: 800, color: MUTED, letterSpacing: .5,
                textTransform: 'uppercase', mb: .75,
              }}>When they come alive</Typography>
              <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
                {data.upcoming.map((u) => (
                  <Tooltip key={u.starts_on}
                    title={`${u.n} membership${u.n === 1 ? '' : 's'} become active on ${u.starts_on}`}>
                    <Box sx={{
                      px: 1.25, py: .75, borderRadius: 2,
                      border: `1px solid ${OUT.starts_later.bd}`,
                      background: OUT.starts_later.bg, minWidth: 92,
                    }}>
                      <Typography sx={{
                        fontSize: 11.5, fontWeight: 800, color: OUT.starts_later.fg,
                      }}>{u.starts_on}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: OUT.starts_later.fg, opacity: .8 }}>
                        {u.n} · {Number(u.days_away) <= 0 ? 'due now' : `in ${u.days_away}d`}
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </>
          )}

          {/*  NHIA's own words on the ones that did not work. Their wording, not
              our paraphrase — it is what somebody will quote back to them. */}
          {data.problems?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{
                fontSize: 10.5, fontWeight: 800, color: MUTED, letterSpacing: .5,
                textTransform: 'uppercase', mb: .75,
              }}>What NHIA said about the ones that did not work</Typography>
              <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
                {data.problems.map((p, i) => (
                  <Chip key={i} size="small" variant="outlined"
                    label={`${p.said} · ${Number(p.n).toLocaleString()}`}
                    sx={{
                      fontSize: 11, maxWidth: 420,
                      borderColor: OUT[p.outcome]?.bd || LINE,
                      color: OUT[p.outcome]?.fg || MUTED,
                    }} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
